/**
 * OQ-327 · Tab "Mẫu thư".
 *
 * Chủ dự án: *"thêm 1 trang cài đặt mẫu email cho tất cả các luồng để tôi có thể
 * tùy biến mẫu email gửi cho từng luồng"*.
 *
 * ── Vì sao tab này có nút Lưu RIÊNG, khác mọi tab khác ────────────────────
 *
 * Năm tab kia ghi vào `draft` chung và nút "Lưu thay đổi" ở đầu trang gửi tất cả
 * một lượt. Ở OQ-287 tôi đã dựng nhầm một nút Lưu thừa và bị bắt đúng chuyện ấy.
 *
 * Nhưng ở đây mẫu thư **không phải khoá cài đặt**: mỗi mẫu là một bản ghi riêng
 * ở bảng `mail_templates`, có đường API riêng và có cả thao tác "khôi phục mặc
 * định" mà `PATCH /settings` không diễn đạt được. Gộp vào nút chung thì hoặc
 * phải bịa khoá cài đặt cho 19 mẫu, hoặc nút chung im lặng bỏ qua mẫu đang sửa —
 * người dùng bấm Lưu, thấy "đã lưu", và mẫu vẫn nguyên.
 *
 * Nên tab này tự lo phần lưu của mình, và nói rõ điều đó ngay dưới ô soạn.
 *
 * ── Một mẫu mở tại một thời điểm ─────────────────────────────────────────
 *
 * 19 ô soạn mở cùng lúc thì không ai đọc nổi, và "đang sửa dở mẫu nào" trở thành
 * câu hỏi không trả lời được. Danh sách bên trái, ô soạn bên phải.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../lib/i18n';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { AsyncSection, SkeletonCard } from '../../components/States';

interface BienMau {
  ten: string;
  moTa: string;
  viDu: string;
}

export interface MauThuDto {
  key: string;
  nhan: string;
  luong: string;
  bien: BienMau[];
  subject: string;
  body: string;
  nutLabel?: string;
  daTuyBien: boolean;
  macDinh: { subject: string; body: string };
  updatedAt?: string;
}

export function MailTemplatePanel(): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const { toast, toastError } = useToast();

  /* Chi tiết đầu tiên thay câu chung — xem OQ-322. */
  const loiGon = (err: unknown): string =>
    err instanceof ApiError ? err.moTaRoRang : t('state.error.title');

  const dsQuery = useQuery({
    queryKey: ['settings', 'mail-templates'],
    queryFn: () => api.get<{ items: MauThuDto[] }>('/settings/mail-templates'),
  });

  const [dangChon, setDangChon] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [xemTruoc, setXemTruoc] = useState<{ subject: string; html: string } | null>(null);

  const ds = useMemo(() => dsQuery.data?.items ?? [], [dsQuery.data]);
  const mau = ds.find((m) => m.key === dangChon) ?? null;

  /*
   * Nạp ô soạn NGAY lúc chọn, không qua `useEffect` theo dõi `ds`.
   *
   * Bản đầu dùng effect và phải tắt `exhaustive-deps` để nó không chạy lại mỗi
   * lượt refetch — mà mỗi lượt chạy lại là một lần nuốt mất phần đang gõ dở.
   * Tắt chốt để giữ một hành vi đúng là dấu hiệu chỗ ấy đang làm sai việc: dữ
   * liệu này chỉ đổi khi người dùng bấm, nên nó thuộc về lượt bấm.
   */
  const chonMau = (m: MauThuDto) => {
    setDangChon(m.key);
    setSubject(m.subject);
    setBody(m.body);
    setXemTruoc(null);
  };

  const daSua = mau !== null && (subject !== mau.subject || body !== mau.body);

  const luu = useMutation({
    mutationFn: (key: string) =>
      api.patch<MauThuDto>(`/settings/mail-templates/${encodeURIComponent(key)}`, {
        subject,
        body,
      }),
    onSuccess: async (m) => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'mail-templates'] });
      setSubject(m.subject);
      setBody(m.body);
      toast(t('settings.mail.saved'));
    },
    onError: (err) => toastError(loiGon(err)),
  });

  const khoiPhuc = useMutation({
    mutationFn: (key: string) =>
      api.del<MauThuDto>(`/settings/mail-templates/${encodeURIComponent(key)}`),
    onSuccess: async (m) => {
      await queryClient.invalidateQueries({ queryKey: ['settings', 'mail-templates'] });
      setSubject(m.subject);
      setBody(m.body);
      setXemTruoc(null);
      toast(t('settings.mail.restored'));
    },
    onError: (err) => toastError(loiGon(err)),
  });

  const xem = useMutation({
    mutationFn: (key: string) =>
      api.post<{ subject: string; html: string }>(
        `/settings/mail-templates/${encodeURIComponent(key)}/preview`,
        { subject, body },
      ),
    onSuccess: (kq) => setXemTruoc(kq),
    onError: (err) => toastError(loiGon(err)),
  });

  return (
    <div className="card-body stack" data-od-id="mail-templates">
      <div className="form-alert" role="note">
        <Icon name="info" size={16} />
        <span>{t('settings.mail.lede')}</span>
      </div>

      <AsyncSection
        loading={dsQuery.isLoading}
        error={dsQuery.error}
        data={dsQuery.data}
        onRetry={() => void dsQuery.refetch()}
        skeleton={<SkeletonCard rows={6} />}
      >
        {() => (
          <div className="grid g-2">
            <div className="card">
              <div className="card-head">
                <div className="grow">
                  <h2>{t('settings.mail.listTitle')}</h2>
                  <div className="sub">{t('settings.mail.listSub', { count: ds.length })}</div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-wrap">
                  <table className="tbl" data-od-id="mail-template-list">
                    <thead>
                      <tr>
                        <th>{t('settings.mail.col.flow')}</th>
                        <th>{t('settings.mail.col.state')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ds.map((m) => (
                        <tr key={m.key} aria-selected={m.key === dangChon}>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              onClick={() => chonMau(m)}
                            >
                              {m.nhan}
                            </button>
                            <div className="sub">{m.luong}</div>
                          </td>
                          <td>
                            {m.daTuyBien ? (
                              <span className="chip warn">{t('settings.mail.state.custom')}</span>
                            ) : (
                              <span className="chip">{t('settings.mail.state.default')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <div className="grow">
                  <h2>{mau ? mau.nhan : t('settings.mail.pickTitle')}</h2>
                  <div className="sub">{mau ? mau.luong : t('settings.mail.pickSub')}</div>
                </div>
              </div>

              {mau === null ? (
                <div className="card-body">
                  <p className="muted">{t('settings.mail.pickHint')}</p>
                </div>
              ) : (
                <div className="card-body stack">
                  <div className="field">
                    <label htmlFor="mail-subject">{t('settings.mail.subject')}</label>
                    <input
                      id="mail-subject"
                      type="text"
                      maxLength={200}
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      data-od-id="mail-subject"
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="mail-body">{t('settings.mail.body')}</label>
                    <textarea
                      id="mail-body"
                      rows={12}
                      maxLength={8000}
                      value={body}
                      onChange={(event) => setBody(event.target.value)}
                      data-od-id="mail-body"
                    />
                    <span className="hint">{t('settings.mail.bodyHint')}</span>
                  </div>

                  <div className="field">
                    <label>{t('settings.mail.vars')}</label>
                    <div className="row">
                      {mau.bien.length === 0 ? (
                        <span className="muted">{t('settings.mail.noVars')}</span>
                      ) : (
                        mau.bien.map((b) => (
                          <span
                            key={b.ten}
                            className="chip mono"
                            title={t('settings.mail.varTip', { moTa: b.moTa, viDu: b.viDu })}
                          >
                            {`{${b.ten}}`}
                          </span>
                        ))
                      )}
                    </div>
                    <span className="hint">{t('settings.mail.varsHint')}</span>
                  </div>

                  <div className="row">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!daSua || luu.isPending}
                      onClick={() => luu.mutate(mau.key)}
                      data-od-id="mail-save"
                    >
                      {luu.isPending ? t('common.processing') : t('settings.mail.save')}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={xem.isPending}
                      onClick={() => xem.mutate(mau.key)}
                      data-od-id="mail-preview"
                    >
                      {t('settings.mail.preview')}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={!mau.daTuyBien || khoiPhuc.isPending}
                      onClick={() => khoiPhuc.mutate(mau.key)}
                      data-od-id="mail-restore"
                    >
                      {t('settings.mail.restore')}
                    </button>
                  </div>

                  <p className="hint">{t('settings.mail.saveNote')}</p>

                  {xemTruoc ? (
                    <div className="field" data-od-id="mail-preview-box">
                      <label>{t('settings.mail.previewTitle')}</label>
                      <div className="sub">{xemTruoc.subject}</div>
                      {/*
                        `srcdoc` chứ không phải `dangerouslySetInnerHTML`: thư mẫu
                        có `<html>`, `<body>` và style riêng. Nhồi thẳng vào trang
                        thì style của thư đè lên style của ứng dụng, và ngược lại
                        xem trước không còn giống thứ người nhận sẽ thấy.
                        Khung riêng cũng chặn luôn mọi thứ trong đó chạm tới trang.
                      */}
                      <iframe
                        title={t('settings.mail.previewTitle')}
                        srcDoc={xemTruoc.html}
                        sandbox=""
                        style={{
                          width: '100%',
                          height: 360,
                          border: '1px solid var(--line, #e2e8f0)',
                          borderRadius: 6,
                          background: '#fff',
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </AsyncSection>
    </div>
  );
}
